import React, { useState, useEffect } from 'react';
import { Activity, TrendingUp, Trophy, X, Search } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import ProgressStatsBlock from '../components/ProgressStatsBlock';

// ── Custom dot: shows reps at max weight above each chart point ──────────────
const RepsDot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={4} fill="var(--primary)" />
      {payload?.reps ? (
        <text x={cx} y={cy - 9} textAnchor="middle" fontSize={9} fontWeight={600} fill="var(--muted-foreground)">
          {payload.reps}r
        </text>
      ) : null}
    </g>
  );
};

// ── Tooltip: shows all sets for the hovered session ───────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const sets = d?.sets || [];
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: 13, minWidth: 110,
    }}>
      <p style={{ color: 'var(--muted-foreground)', marginBottom: 6, fontSize: 11 }}>{label}</p>
      {sets.length > 0 ? (
        sets.map((s, i) => (
          <p key={i} style={{ margin: '2px 0', fontWeight: 600, color: 'var(--foreground)', fontSize: 12 }}>
            <span style={{ color: 'var(--muted-foreground)', fontWeight: 400 }}>S{i + 1} </span>
            <span style={{ color: 'var(--primary)' }}>{s.weight}kg</span>
            {' × '}
            {s.reps} reps
          </p>
        ))
      ) : (
        <>
          <p style={{ fontWeight: 700, color: 'var(--primary)', margin: 0 }}>{d?.peso} kg</p>
          {d?.reps ? <p style={{ color: 'var(--foreground)', fontSize: 12, margin: '2px 0 0' }}>{d.reps} reps</p> : null}
        </>
      )}
    </div>
  );
};

// ── Adaptive Y-axis ticks ─────────────────────────────────────────────────────
const computeTicks = (data) => {
  const weights = data.map((d) => d.peso).filter((w) => typeof w === 'number' && w > 0);
  if (weights.length === 0) return {};
  const minW = Math.min(...weights);
  const maxW = Math.max(...weights);
  if (minW === maxW) {
    const step = 0.5;
    return { ticks: [minW - step, minW, minW + step], domain: [minW - step * 2, minW + step * 2] };
  }
  const range = maxW - minW;
  const step = range <= 3 ? 0.25 : range <= 10 ? 0.5 : range <= 30 ? 1 : 2.5;
  const tickMin = Math.floor(minW / step) * step;
  const tickMax = Math.ceil(maxW / step) * step;
  const ticks = [];
  for (let v = tickMin - step; v <= tickMax + step + 0.001; v += step) {
    ticks.push(Math.round(v * 1000) / 1000);
  }
  return { ticks, domain: [tickMin - step, tickMax + step] };
};

const tickLabel = (v) => (v % 1 === 0 ? String(v) : String(parseFloat(v.toFixed(2))));

// ── PR calculation using all per-set data ─────────────────────────────────────
const computeRecords = (exercises) => {
  return Object.entries(exercises)
    .map(([key, entries]) => {
      const name = entries[0]?.exercise_name || key;

      const allSets = [];
      entries.forEach((entry) => {
        const date = entry.date;
        if (entry.sets?.length > 0) {
          entry.sets.forEach((s) => {
            if ((s.weight || 0) > 0 || (s.reps || 0) > 0) {
              allSets.push({ weight: s.weight || 0, reps: s.reps || 0, date });
            }
          });
        } else if ((entry.weight || 0) > 0) {
          allSets.push({ weight: entry.weight, reps: entry.reps || 0, date });
        }
      });

      if (allSets.length === 0) return null;

      const maxWeightSet = allSets.reduce((best, s) => (s.weight > best.weight ? s : best), allSets[0]);
      const repsAtMaxWeight = allSets
        .filter((s) => s.weight === maxWeightSet.weight)
        .reduce((max, s) => Math.max(max, s.reps || 0), 0);
      const maxRepsSet = allSets.reduce(
        (best, s) => (s.reps || 0) > (best.reps || 0) ? s : best,
        allSets[0]
      );

      return {
        name,
        maxWeight: maxWeightSet.weight,
        repsAtMaxWeight: repsAtMaxWeight || null,
        maxReps: maxRepsSet.reps || null,
        weightAtMaxReps: maxRepsSet.weight || 0,
      };
    })
    .filter(Boolean)
    .filter((r) => r.maxWeight > 0 || r.maxReps > 0);
};

// ── PR Modal ──────────────────────────────────────────────────────────────────
const MaxRMModal = ({ exercises, onClose, t }) => {
  const [search, setSearch] = useState('');
  const records = computeRecords(exercises);
  const filtered = search.trim()
    ? records.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
    : records;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)', padding: '24px 16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--background)', width: '100%', maxWidth: 480,
        borderRadius: 16, overflow: 'hidden', maxHeight: '82vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
          background: 'var(--secondary)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trophy size={18} color="var(--primary)" />
            <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--foreground)' }}>
              {t('progress.max_rm_title')}
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted-foreground)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--secondary)', flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)', pointerEvents: 'none' }} />
            <input
              type="text"
              className="input"
              style={{ paddingLeft: 30, marginBottom: 0 }}
              placeholder={t('progress.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Column headers */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 0,
          background: 'var(--secondary)', padding: '9px 16px',
          borderBottom: '1px solid var(--border)', flexShrink: 0,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('progress.col_exercise')}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'right', minWidth: 82 }}>
            {t('progress.col_max_weight')}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'right', minWidth: 76 }}>
            {t('progress.col_max_reps')}
          </span>
        </div>

        {/* Rows */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 ? (
            <p style={{ color: 'var(--muted-foreground)', padding: 24, textAlign: 'center', fontSize: 14 }}>
              {t('progress.no_data_desc')}
            </p>
          ) : filtered.map((rec, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 0,
              padding: '12px 16px',
              borderBottom: i < filtered.length - 1 ? '1px solid rgba(39,39,42,0.7)' : 'none',
              background: i % 2 === 0 ? 'transparent' : 'rgba(39,39,42,0.25)',
              alignItems: 'center',
            }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--foreground)', lineHeight: 1.3, margin: 0 }}>
                {rec.name}
              </p>
              <div style={{ textAlign: 'right', paddingLeft: 12, minWidth: 82 }}>
                {rec.maxWeight > 0 ? (
                  <>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)', display: 'block', lineHeight: 1 }}>
                      {rec.maxWeight} kg
                    </span>
                    {rec.repsAtMaxWeight ? (
                      <span style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>× {rec.repsAtMaxWeight} reps</span>
                    ) : null}
                  </>
                ) : <span style={{ fontSize: 14, color: 'var(--muted-foreground)' }}>—</span>}
              </div>
              <div style={{ textAlign: 'right', paddingLeft: 12, minWidth: 76 }}>
                {rec.maxReps ? (
                  <>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--foreground)', display: 'block', lineHeight: 1 }}>
                      {rec.maxReps}
                    </span>
                    {rec.weightAtMaxReps > 0 ? (
                      <span style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>× {rec.weightAtMaxReps} kg</span>
                    ) : null}
                  </>
                ) : <span style={{ fontSize: 14, color: 'var(--muted-foreground)' }}>—</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const ProgressPage = () => {
  const [progress, setProgress] = useState({});
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWorkoutId, setActiveWorkoutId] = useState(null);
  const [showMaxRM, setShowMaxRM] = useState(false);
  const [searchExercise, setSearchExercise] = useState('');
  const { t } = useTranslation();

  useEffect(() => { fetchProgress(); }, []);

  useEffect(() => {
    const ids = Object.keys(progress || {});
    if (ids.length > 0 && !activeWorkoutId) setActiveWorkoutId(ids[0]);
  }, [progress, activeWorkoutId]);

  const fetchProgress = async () => {
    try {
      const [progressRes, statsRes] = await Promise.all([
        apiFetch('/api/progress'),
        apiFetch('/api/dashboard/stats'),
      ]);
      if (progressRes.ok) setProgress(await progressRes.json());
      if (statsRes.ok) setDashboardStats(await statsRes.json());
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  const workoutIds = Object.keys(progress).sort(
    (a, b) => progress[b].sessions_count - progress[a].sessions_count
  );
  const activeData = activeWorkoutId ? progress[activeWorkoutId] : null;

  return (
    <>
      <div className="page-container animate-fade-in" data-testid="progress-page">
        {/* Header */}
        <div className="header" style={{ alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className="header-title" style={{ margin: 0 }}>{t('progress.title')}</h1>
            <TrendingUp size={26} color="var(--primary)" />
          </div>
          {activeData && (
            <button
              onClick={() => setShowMaxRM(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: '999px',
                fontSize: 13, fontWeight: 600,
                border: '1px solid var(--primary)',
                background: 'rgba(var(--primary-rgb),0.10)',
                color: 'var(--primary)', cursor: 'pointer',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              <Trophy size={14} />
              {t('progress.max_rm_btn')}
            </button>
          )}
        </div>

        {/* Weekly summary block (moved from Dashboard) */}
        {dashboardStats && <ProgressStatsBlock progress={dashboardStats} />}

        {/* Routine pills */}
        {workoutIds.length > 0 && (
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 14, paddingBottom: 4 }}>
            {workoutIds.map((id) => {
              const isActive = id === activeWorkoutId;
              return (
                <button
                  key={id}
                  onClick={() => { setActiveWorkoutId(id); setSearchExercise(''); }}
                  style={{
                    padding: '8px 14px', borderRadius: '999px', fontSize: 13,
                    fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
                    border: isActive ? '1px solid var(--primary)' : '1px solid var(--border)',
                    background: isActive ? 'rgba(var(--primary-rgb),0.15)' : 'var(--secondary)',
                    color: 'var(--foreground)',
                  }}
                >
                  {progress[id].workout_name}
                </button>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {workoutIds.length === 0 || !activeWorkoutId ? (
          <div className="empty-state">
            <Activity className="empty-icon" />
            <h3 className="empty-title">{t('progress.no_data_title')}</h3>
            <p className="empty-desc">{t('progress.no_data_desc')}</p>
          </div>
        ) : (
          workoutIds
            .filter((id) => id === activeWorkoutId)
            .map((workoutId) => {
              const data = progress[workoutId];
              const exerciseIds = Object.keys(data.exercises || {});

              // Filter by search
              const filteredIds = searchExercise.trim()
                ? exerciseIds.filter((exIdx) => {
                    const name = (data.exercises[exIdx][0]?.exercise_name || '').toLowerCase();
                    return name.includes(searchExercise.toLowerCase());
                  })
                : exerciseIds;

              return (
                <div key={workoutId} className="mb-6">
                  <h2 style={{ fontSize: 18, marginBottom: 12, color: 'var(--foreground)' }}>
                    {data.workout_name}
                    <span className="text-muted" style={{ fontSize: 14, marginLeft: 8 }}>
                      {t('progress.sessions_count', { count: data.sessions_count })}
                    </span>
                  </h2>

                  {/* Exercise search */}
                  <div style={{ position: 'relative', marginBottom: 16 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted-foreground)', pointerEvents: 'none' }} />
                    <input
                      type="text"
                      className="input"
                      style={{ paddingLeft: 30, marginBottom: 0 }}
                      placeholder={t('progress.search_placeholder')}
                      value={searchExercise}
                      onChange={(e) => setSearchExercise(e.target.value)}
                    />
                  </div>

                  {filteredIds.length === 0 && (
                    <p style={{ color: 'var(--muted-foreground)', fontSize: 14, textAlign: 'center', padding: '24px 0' }}>
                      {t('progress.no_data_desc')}
                    </p>
                  )}

                  {filteredIds.map((exIdx) => {
                    const exerciseData = data.exercises[exIdx];
                    const sorted = [...exerciseData].sort((a, b) => new Date(a.date) - new Date(b.date));
                    const exerciseName = sorted[0]?.exercise_name || `Ejercicio ${parseInt(exIdx) + 1}`;

                    const chartData = sorted.map((d) => ({
                      date: d.date.split('-').slice(1).join('/'),
                      peso: d.weight,
                      reps: d.reps != null ? String(d.reps) : '',
                      sets: d.sets || [],
                    }));

                    const { ticks, domain } = computeTicks(chartData);

                    // Last 3 sessions with set detail
                    const recentWithSets = sorted
                      .filter((d) => d.sets?.length > 0)
                      .slice(-3)
                      .reverse();

                    return (
                      <div key={exIdx} className="chart-container mb-4">
                        <h3 className="chart-title">{exerciseName}</h3>

                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={chartData} margin={{ top: 22, right: 8, bottom: 0, left: 0 }}>
                            <XAxis dataKey="date" stroke="var(--muted-foreground)" fontSize={10} />
                            <YAxis
                              stroke="var(--muted-foreground)"
                              fontSize={10}
                              width={44}
                              ticks={ticks}
                              domain={domain}
                              tickFormatter={tickLabel}
                            />
                            <Tooltip content={<ChartTooltip />} />
                            <Line
                              type="monotone"
                              dataKey="peso"
                              stroke="var(--primary)"
                              strokeWidth={2}
                              dot={<RepsDot />}
                              activeDot={{ r: 5, fill: 'var(--primary)' }}
                            />
                          </LineChart>
                        </ResponsiveContainer>

                        {/* Per-session set detail */}
                        {recentWithSets.length > 0 && (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                              {t('progress.recent_sets')}
                            </p>
                            {recentWithSets.map((session, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: i < recentWithSets.length - 1 ? 6 : 0, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 11, color: 'var(--muted-foreground)', minWidth: 34, flexShrink: 0 }}>
                                  {session.date.split('-').slice(1).join('/')}
                                </span>
                                {session.sets.map((s, j) => {
                                  const isMax = s.weight === Math.max(...session.sets.map((x) => x.weight));
                                  return (
                                    <span key={j} style={{
                                      fontSize: 11, fontWeight: 600,
                                      background: isMax ? 'rgba(var(--primary-rgb),0.14)' : 'rgba(255,255,255,0.055)',
                                      border: `1px solid ${isMax ? 'rgba(var(--primary-rgb),0.32)' : 'rgba(255,255,255,0.1)'}`,
                                      color: isMax ? 'var(--primary)' : 'var(--foreground)',
                                      borderRadius: 6, padding: '2px 7px',
                                    }}>
                                      {s.weight}×{s.reps}
                                    </span>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
        )}
      </div>

      {showMaxRM && activeData && (
        <MaxRMModal
          exercises={activeData.exercises}
          onClose={() => setShowMaxRM(false)}
          t={t}
        />
      )}
    </>
  );
};

export default ProgressPage;
